// #region Imports

import React, { useEffect, useState, useRef } from "react";
import * as Modal from 'react-modal';
import { connect, useDispatch, useSelector} from 'react-redux';
import { IStateTree } from '../redux/store';
import { actionCreators, setNotesPanelExpaned, forceEditNote, aNoteWasUpdated } from '../redux/modules/document';
import { INoteData } from '../dto/INoteData';
import { NoteService } from '../services/noteService';
import { SelectorService } from '../services/SelectorService';
import NoteInlineEditor from '../components/noteInlineEditor';
import { Toaster } from "../modules/ui/toaster";
import { OverlappingNoteComponent } from "./NoteComponent";

// #endregion Imports

// #region Dispatchers

export interface INoteComponentDispatch {
    onNoteDeleted?: (note: INoteData) => void;
    onNoteOpenEdit?: (note: INoteData) => void;
    onNoteOpenToggleVisibility?: (visibility: boolean) => void;
    resetRightPanel?: ()=> void;
    forceEditNote?: (note: INoteData)=> void;
}

// #endregion Dispatchers

// #region Props

export interface INoteComponentProps extends INoteComponentDispatch {
    note: INoteData;
    position?: boolean;
    currentNote: INoteData,
    rightPanelOpen: boolean;
    aNoteWasUpdated?: number;
    index?: number;
    categories: [];
    navHighlighted?: boolean;
    hoverHighlighted?: boolean;
}

// #endregion Props

// #region State

export interface INoteComponentState {
    top: number;
    showNoteContent: boolean;
    showEditor: boolean;
    isDeleteDialogOpen: boolean;
    isScrollingToNoteCurrently: boolean;
}

// #endregion State

// #region Component

export class NoteComponent extends React.Component<INoteComponentProps, INoteComponentState> {
    private timeoutId: NodeJS.Timeout | null = null;
    private debounceTimer;
    
    constructor(props) {
        super(props);

        const top = this.getTopPosition();

        this.state = {
            top: top,
            showNoteContent: true,
            showEditor: false,
            isDeleteDialogOpen: false,
            isScrollingToNoteCurrently: false,
        };

        this.debounceTimer = null;
    }

    componentDidUpdate(prevProps: Readonly<INoteComponentProps>, prevState: Readonly<INoteComponentState>) {
        if (prevProps.aNoteWasUpdated !== this.props.aNoteWasUpdated) {
            this.updatePosition();
        }        
    }


 
    private getTopPosition() {
        if (this.props.note.elementId === '#documentcontent') {
            // Om anteckningen är knuten till dokumentroten
            return 15;
        }
        if(this.props.note.elementId === "#"){
            return 15;
        }
        const element = $(this.props.note.elementId);
        if (element.length === 0) {
            console.log("element not found");
            // Om elementet för anteckningen inte hittas fall tillbaka på dokumentroten
            return 15;
        }
        
        const position = element.length === 0 ? 0 : element.position().top;
        return position > 15 ? position : 15;
    }

    private scrollToNote() {
        if(window.innerWidth >= 1200){
            this.props.onNoteOpenToggleVisibility && this.props.onNoteOpenToggleVisibility(true);
        }        

        let position = $("#" + this.props.note.id).offset()?.top;
        position -= 300;

        $([document.documentElement, document.body]).animate({
            scrollTop: position
        }, 500);
        
    }

    private moveBundleItemsInDOM(){
        if($("#"+this.props.note.id).attr("data-colliding") !== undefined){
            const belongsToId = $("#"+this.props.note.id).attr("data-colliding");
            const firstBundleItemArray = $('[data-colliding="'+belongsToId+'"]');
            firstBundleItemArray?.map((i, item)=>{
                let collidingIndex = $("#"+item.id).attr("data-collidingindex");
                if(!!collidingIndex){
                    const ix = Number.parseInt(collidingIndex);
                    if(ix === 0){
                        $("#"+item.id).addClass("bundleMaster");
                    }else{
                        const masterBundler = $("#"+firstBundleItemArray[0].id).parent().parent().find(".bundleIndex__0");
                        // Find the target div where you want to move the element and append it
                        masterBundler.append($("#note-editor-"+item.id));

                    }
                }
            });
        }
    }


    private updatePosition = () => {
        this.debounce(() => {
            const top = this.getTopPosition();
            this.setState({ top: top }); 
            this.moveBundleItemsInDOM();
        }, 300);
    }
        
    private toggleEditor = () => {
        $('.content__left-column').removeClass('content__left-column--open');
        if(window.innerWidth <= 1200){
            this.props.forceEditNote && this.props.forceEditNote(this.props.note);
        }else{
            const openEditor = !this.state.showEditor;
            this.setState({showEditor: openEditor})   
            if(openEditor) { 
                document.body.classList.add('note-editor-open');            
                if (this.props.onNoteOpenEdit) {
                    this.props.onNoteOpenEdit(this.props.note);
                }                        
            } else {
                document.body.classList.remove('note-editor-open');
            }
            // if clicked on in mobile view
            if(!this.state.isScrollingToNoteCurrently){
                this.scrollToNote();
            }
        }
    }

    private openEditor = () => {
        this.setState({showEditor: true})   
        document.body.classList.add('note-editor-open');            
        if (this.props.onNoteOpenEdit) {
            this.props.onNoteOpenEdit(this.props.note);
        }   
    }
    
    private hideEditor = () => {
        this.setState({showEditor: false});
        document.body.classList.remove('note-editor-open');
    }

    /*Delete functions*/
    private onDeleteClick = () => {
        this.openDeleteDialog();
    }

    private openDeleteDialog = () => {
        this.setState({ isDeleteDialogOpen: true });
    }    

    private closeDeleteDialog = () => {
        this.setState({ isDeleteDialogOpen: false });
    }
    
    private onConfirmDelete = () => {
        const service = new NoteService();

        service.deleteNoteById(this.props.note.id,
            () => {
                this.onNoteDeleted();
            },
            () => {
                alert('Ett fel har uppstått. Anteckningen kunde inte tas bort');
            });
    }    

    private onNoteDeleted = () => {
        if (this.props.onNoteDeleted) {
            this.props.onNoteDeleted(this.props.note);
            Toaster.show("Anteckning raderad", true);
        }
        if(this.props.resetRightPanel){
            this.props.resetRightPanel();            
            this.setState({ isDeleteDialogOpen: false });
            document.body.classList.remove('note-editor-open');
        }
        if (this.props.note.selection != null) {
            SelectorService.instance.remove(this.props.note.elementId);
        }
    }

    private isNoteWithText = () => {
       return (this.props.note.text !== null && this.props.note.text.length > 0) ||  (this.props.note.title !== null && this.props.note.title.length > 0);
    }
    
    private renderNoteContent = () => {
        const note = this.props.note;
        const activeClass = this.state.showEditor ? 'note__content--active' : '';

        if(this.isNoteWithText()) {
            let text = '';
            if (note.title != null && note.title.length > 0) {
                text = note.title;
            }
            else if (note.text != null && note.text.length > 0) {
                text = note.text;
            }            
            
            return (
                <div className="noteFold">
                    <div className={`note__content ${activeClass}`} onKeyUp={(e)=>{e.key === "Enter" && this.toggleEditor.bind(this)()}} data-color={note.color} aria-label={note.title ?? note.text} role="button" id={note.id} title={note.title} onClick={this.toggleEditor.bind(this)}> 
                        <div className="note__icon note__icon--note-with-text"/>
                    </div>
                </div>
            );            
        } else {
            return (
                <div className="noteFold">
                    <div className={`note__content ${activeClass}`} onKeyUp={(e)=>{e.key === "Enter" && this.toggleEditor.bind(this)()}} data-color={note.color} aria-label={note.title ?? note.text} id={note.id} role="button" title={note.title} onClick={this.toggleEditor.bind(this)}> 
                        <div className="note__icon note__icon--marking"/>
                    </div>
                </div>
            );
        }        
    }

    render() {
        const note = this.props.note;
        const currentNote = this.props.currentNote;
        const style = this.props.position ? { top: this.state.top-40 } : undefined;
        
        Modal.setAppElement('#document-main');
        return (
            <>
                <div id={"wrappingID_" + this.props.note.id} className={`note note--${note.color}  ${currentNote && currentNote.id == note.id ? 'note--selected' : ''} ${this.props.position ? 'note--floating' : 'note--in-list'} ${this.isNoteWithText() ? 'note--with-text' : ''} ${this.props.navHighlighted || this.props.hoverHighlighted ? 'note--nav-highlight' : ''}`}  style={style}>                  
                    <>
                    
                    {this.renderNoteContent()}

                    <OverlappingNoteComponent note={note} onCancel={this.hideEditor} rightPanelOpenState={this.props.rightPanelOpen} onDelete={this.openDeleteDialog} forceOpenEditor={this.triggerChildFunction} categories={this.props.categories}/>
                    
                    </>       
                </div>
                <Modal isOpen={this.state.isDeleteDialogOpen} onRequestClose={this.closeDeleteDialog} overlayClassName="modal__overlay" className="modal__window" contentLabel="Ta bort anteckning">
                    <button onClick={this.closeDeleteDialog} className="modal__close">Stäng</button>

                    <div className="modal__content">
                        <h3 className="modal__title">Vill du radera din anteckning?</h3>
                        <p>Din anteckning kommer raderas och du kan inte återskapa den.</p>

                        <div className="modalCTAs">
                            <button onClick={this.closeDeleteDialog} className="button__primary">Radera inte</button>
                            <button onClick={this.onConfirmDelete} className="button__secondary">Radera</button>
                        </div>
                    </div>
                </Modal>                     
            </>
        );             
    }

    debounce = (func, delay) => {
        if (this.debounceTimer) {
          clearTimeout(this.debounceTimer);
        }
    
        this.debounceTimer = setTimeout(func, delay);
      }

    componentDidMount() {
        window.addEventListener('resize', this.updatePosition);
        if(this.props.note.openEditorAtStart) {
            this.setState({showEditor: true})
        }  
        this.updatePosition();
        this.moveBundleItemsInDOM();   
    }
 
}

// #endregion Component

// #region Redux mappers

function mapStateToProps(state: IStateTree, ownProps): INoteComponentProps {
    return { 
        note: ownProps.note, 
        position: ownProps.position,
        currentNote: state.document.currentNote,
        rightPanelOpen: state.document.notesPanelExpaned,
        aNoteWasUpdated: state.document.aNoteWasUpdated
    }
}

function mapDispatchToProps(dispatch): INoteComponentDispatch {
    return {
        onNoteDeleted: (note: INoteData) => {
            dispatch(actionCreators.removeNote(note));
        },        
        onNoteOpenEdit: (note: INoteData) => {
            dispatch(actionCreators.setCurrentNote(note));
        },        
        onNoteOpenToggleVisibility: (visibility: boolean) => {
            dispatch(actionCreators.setNotesPanelExpaned(visibility));
            dispatch(actionCreators.setRollDownExpanded(false));
        },
        resetRightPanel: ()=>{
            dispatch(actionCreators.setRollDownExpanded(false));
        },
        forceEditNote: (note: INoteData)=>{
            dispatch(actionCreators.forceEditNote(note));
        }
    }
}

// #endregion Redux mappers

// #region Smart container

export const NoteContainer = connect(mapStateToProps, mapDispatchToProps)(NoteComponent);

// #endregion Smart container
